/**
 * ChartStack — empilha N painéis de chart pra um mesmo equipment.
 *
 * Os painéis compartilham o MESMO syncKey → cursor sincronizado entre todos.
 * Layout do usuário (qual métrica em cada painel) é persistido em localStorage
 * via useChartLayout(equipmentId).
 *
 * O parent (EquipmentDetail) fornece os dados já transformados por métrica.
 */

import { useMemo } from "react";
import { Plus, RotateCcw } from "lucide-react";

import { Button } from "@/components/Button";
import {
  ChartPanel,
  type AvailableSeries,
  type DoorStats,
  type MetricKey,
} from "./ChartPanel";
import { useChartLayout } from "./useChartLayout";
import type { ChartSeries, DoorEvent } from "./MetricChart";

interface Props {
  /** Pra persistir layout no localStorage. */
  equipmentId: string;
  /** Timestamps em segundos epoch, compartilhados entre todas as métricas. */
  timestamps: number[];
  /** Map métrica → série(s) plotada(s). Pra modos pré-definidos. */
  seriesByMetric: Record<Exclude<MetricKey, "custom">, ChartSeries[]>;
  /** Catálogo completo pra modo "custom" — usuário escolhe quais plotar. */
  availableSeries?: AvailableSeries[];
  /** Eventos de porta aberta — overlay opcional. */
  doorEvents?: DoorEvent[];
  loading?: boolean;
}

/**
 * Pega séries selecionadas pelo user no modo custom e atribui axis Y
 * automaticamente: primeira unidade → axis 0 (esquerda), segunda → axis 1
 * (direita), demais voltam pra axis 0 (compartilham escala — aceitável).
 */
function buildCustomSeries(
  available: AvailableSeries[],
  ids: string[],
): ChartSeries[] {
  const selected = ids
    .map((id) => available.find((s) => s.id === id))
    .filter((s): s is AvailableSeries => s !== undefined);

  if (selected.length === 0) return [];

  const firstUnit = selected[0].unit;
  const secondUnit = selected.find((s) => s.unit !== firstUnit)?.unit;

  return selected.map((s) => ({
    label: s.label,
    values: s.values,
    color: s.color,
    width: s.width,
    axis: (secondUnit && s.unit === secondUnit ? 1 : 0) as 0 | 1,
    unit: s.unit,
  }));
}

/**
 * Agrega doorEvents em count + tempo total aberto (segundos).
 * Eventos "ainda aberta" (closedAt=null) usam o último timestamp visível.
 */
function computeDoorStats(events: DoorEvent[], lastTs: number | null): DoorStats {
  let count = 0;
  let totalSec = 0;
  for (const ev of events) {
    count++;
    const end = ev.closedAt ?? lastTs ?? ev.openedAt;
    totalSec += Math.max(0, end - ev.openedAt);
  }
  return { count, totalSec: Math.round(totalSec) };
}

export function ChartStack({
  equipmentId,
  timestamps,
  seriesByMetric,
  availableSeries = [],
  doorEvents = [],
  loading,
}: Props) {
  const { panels, addPanel, removePanel, updatePanel, resetLayout } = useChartLayout(equipmentId);

  // Todos os painéis compartilham o mesmo sync key → cursor sincronizado
  const syncKey = `eq-${equipmentId}`;

  // Stats agregados — computa uma vez e passa pra cada painel.
  const doorStats = useMemo(() => {
    const lastTs = timestamps.length ? timestamps[timestamps.length - 1] : null;
    return computeDoorStats(doorEvents, lastTs);
  }, [doorEvents, timestamps]);

  return (
    <div className="space-y-3">
      {panels.map((p) => {
        const series =
          p.metric === "custom"
            ? buildCustomSeries(availableSeries, p.customSeriesIds ?? [])
            : seriesByMetric[p.metric as Exclude<MetricKey, "custom">] ?? [];
        return (
          <ChartPanel
            key={p.id}
            panelId={p.id}
            metric={p.metric}
            onMetricChange={(m) => updatePanel(p.id, { metric: m })}
            showDoor={p.showDoor}
            onShowDoorChange={(v) => updatePanel(p.id, { showDoor: v })}
            onRemove={panels.length > 1 ? () => removePanel(p.id) : undefined}
            timestamps={timestamps}
            series={series}
            doorEvents={doorEvents}
            doorStats={doorStats}
            loading={loading}
            syncKey={syncKey}
            availableSeries={availableSeries}
            customSeriesIds={p.customSeriesIds}
            onCustomSeriesChange={(ids) =>
              updatePanel(p.id, { customSeriesIds: ids })
            }
          />
        );
      })}

      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          onClick={addPanel}
        >
          Adicionar painel
        </Button>
        {panels.length > 2 && (
          <button
            type="button"
            onClick={resetLayout}
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            <RotateCcw className="h-3 w-3" />
            Restaurar layout padrão
          </button>
        )}
      </div>
    </div>
  );
}
